import React, { useContext, useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { DatabaseContext } from '../../../redux/DatabaseStore';
import { useFormik } from 'formik';
import { getRequest, postRequest, deleteRequest, tabulatorGet } from '../../../api/ApiController';
import { cleanSorters, extractSafePaginationParams, createDedupedAjaxRequest, refreshProgressiveFromStart, safeParse, applyUserMaskRowToTabulator, mergePagedStandardListFiltersAndSorters, appendLiteralFieldTrimPagedFilter } from '../../../utils/tabulatorProgressiveLoading';
import { getTabulatorPersistLayoutEvents } from '../../../utils/tabulatorPersistLayout';
import { getEffectiveMask } from '../../../utils/effectiveMask';
import {
  buildMaintenanceMaskAkcieSubItems,
  createConfirmSaveStandardMask,
  createConfirmApplyStandardLayoutToUserMask,
  createSaveUserMaskColumns,
  createOpenMaskDialog,
  createMaskSetFieldHandler,
  createMaskSelectAllHandler,
  createMaskDeselectAllHandler,
  applyDataColumnVisibilityToTable,
  hasApplicableStandardMaskRow,
  MAINTENANCE_MASK_DIALOG_SKIP_FIELDS,
  ensureMandatoryIdVisible,
} from '../../../utils/maintenanceTabulatorMaskActions';
import { inputOnEnterHeaderFilter } from '../../../utils/inputOnEnterHeaderFilter';
import { buildAuditTabulatorColumns, mergeColumnVisibilityWithAuditDefaults } from '../../../utils/tabulatorAuditColumns';
import { SaveIcon, AddIcon, CopyIcon, PrintIcon, RefreshIcon, VerticalLineIcon, BackToFirstIcon, BackIcon, ForwardIcon, ForwardToLastIcon, FavoriteIcon, PriceIcon, SaveAndCloseIcon,EditRowIcon,DeleteIcon, EditIcon, PaperClipIcon, ActivateIcon, DeactivateIcon} from "../../svg/svgIcons";
import { Dialog } from 'primereact/dialog';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import AuthContext from '../../../components/AuthContext';
import { Splitter, SplitterPanel } from 'primereact/splitter';
import { ReactTabulator,reactFormatter } from 'react-tabulator';
import "primereact/resources/themes/lara-light-indigo/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css"
import '../../../assets/css/style.css';
import '../../../assets/css/iconsmenu.css'
import UtilContext from '../../../components/UtilContext';
import { MaintenanceMaskDialogCheckbox } from '../../../components/maintenance/MaintenanceMaskDialogCheckbox';
import { PanelMenu } from 'primereact/panelmenu';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from "primereact/dropdown";
import { OverflowMenuProvider } from '../../../components/toolbar/OverflowMenu';
import IntersectionObserverWrapper from '../../../components/toolbar/IntersectionObserverWrap';
import { createAsyncNavigateHandlers } from '../../../utils/asyncArrayNavigate';
import { navigationContextToQueryParams } from '../../../utils/maintenanceNavigationContext';
import { useMaintenanceTypes } from '../../../utils/useMaintenanceTypes';
import { useMaintenanceTemplates } from '../../../utils/useMaintenanceTemplates';
import { primeMultiSelectHeaderFilter } from '../../../utils/primeMultiSelectHeaderFilter';
import { getPusherChannel } from '../../../utils/pusherChannel';
import { LocaleContext } from '../../../components/LocaleContext';
import { defaultProductLogoSrc } from '../../../constants/brandingDisplay';
import {
  buildCanonicalPrintFooterHtml,
  buildCanonicalPrintHeaderHtml,
} from '../../../utils/printChrome';
import { uiT } from '../../../utils/uiTranslate';

const MASK_PAGE = 'MaintenanceTemplateRecordAdd';

const DEFAULT_MAINTENANCE_TEMPLATE_RECORD_ADD_COLUMN_VISIBILITY = Object.freeze({
  id: true,
  'maintenance_type.name': true,
  'maintenance_type.type': true,
  'maintenance_type.norm': true,
  'maintenance_type.machine_type.name': true,
  'maintenance_type.interval_months': true,
  'responsible.name': true,
  'maintenance_type.description': true,
  required_attachment: true,
  machine_part: true,
  created_at: true,
  updated_at: true,
});

function mergeMaintenanceTemplateRecordAddColumnVisibilityFromSaved(saved) {
  return mergeColumnVisibilityWithAuditDefaults(DEFAULT_MAINTENANCE_TEMPLATE_RECORD_ADD_COLUMN_VISIBILITY, saved);
}

const MASK_DIALOG_HELP_TEXT =
  uiT('MaintenanceTemplateList.MaskHelp');

function applyMaintenanceTemplateRecordAddDataColumnVisibility(table, visibility) {
  applyDataColumnVisibilityToTable(table, DEFAULT_MAINTENANCE_TEMPLATE_RECORD_ADD_COLUMN_VISIBILITY, visibility);
}

function mergeMaintenanceTemplateRecordAddMaskDialogVisibility(partial) {
  return ensureMandatoryIdVisible(
    mergeMaintenanceTemplateRecordAddColumnVisibilityFromSaved(partial),
    DEFAULT_MAINTENANCE_TEMPLATE_RECORD_ADD_COLUMN_VISIBILITY
  );
}

export default function MaintenanceTemplateRecordAdd({maintenance_template, type, navigationContext}) {

    const [state,dispatch] = useContext(DatabaseContext);
    const { t } = useContext(LocaleContext);
    const {setDialogText,setVisible,openFavoriteDialog,toast,setFastView,setUserMaskFilter,setStandardMaskFilter,getDateFormat,getUserColor,getUserInitials,openFilePreview, openChosenNotificationPreferences} = useContext(UtilContext);
    const {user} = useContext(AuthContext);
    const { maintenanceTypes: allMaintenanceTypes } = useMaintenanceTypes();
    const { maintenanceTemplates } = useMaintenanceTemplates();
    const currentTemplateIdRef = useRef(maintenance_template?.id ?? null);
    const prevTemplateIdRef = useRef(maintenance_template?.id);

    const [closeTab,setCloseTab] = useState(false);
    const ref = useRef(null);
    const submitRef = useRef(null);
    const overlayRef = useRef(null);
    const inFlightRef = useRef({ sig: null, promise: null });
    const completedCacheRef = useRef(new Map());
    const savedHScrollRef = useRef({ left: 0, pending: false });
    const tableBuiltRef = useRef(false);
    const isRestoringRef = useRef(false);

    const pageSizePc = useMemo(() => {
        const v = (state.module_settings || []).find(s => s.module === 'global' && s.key === 'page_size_pc')?.value;
        const n = parseInt(v, 10);
        return (Number.isFinite(n) && n > 0) ? n : 50;
    }, [state.module_settings]);

    const getTable = useCallback(() => ref.current?.table ?? ref.current, []);
    const captureHScroll = useCallback(() => {
        const table = getTable();
        if (!table) return;
        const holder = table.rowManager?.element;
        if (!holder) return;
        savedHScrollRef.current = { left: holder.scrollLeft, pending: true };
    }, [getTable]);
    const restoreHScroll = useCallback(() => {
        if (!savedHScrollRef.current.pending) return;
        const table = getTable();
        if (!table) return;
        const holder = table.rowManager?.element;
        const header = table.columnManager?.contentsElement;
        if (!holder) return;
        const left = savedHScrollRef.current.left;
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                holder.scrollLeft = left;
                if (header) header.scrollLeft = left;
                savedHScrollRef.current.pending = false;
            });
        });
    }, [getTable]);
    const refreshFromStart = useCallback(() => refreshProgressiveFromStart(getTable, completedCacheRef, t('Common.LoadingEllipsis')), [getTable]);

    const [formTemplate, setFormTemplate] = useState(maintenance_template);
    const [templatePreview, setTemplatePreview] = useState(null);

    useEffect(() => {
        currentTemplateIdRef.current = formTemplate?.id ?? null;
    }, [formTemplate?.id]);

    const buildRequestParams = useCallback((paramsFromTabulator = {}) => {
        const templateId = currentTemplateIdRef.current;
        const extra = { maintenance_template_id: templateId, ...extractSafePaginationParams(paramsFromTabulator) };
        if (extra.size == null) extra.size = pageSizePc;
        mergePagedStandardListFiltersAndSorters(extra, {
            getTable,
            tableBuiltForParams: tableBuiltRef.current,
            userMaskFilters: state.user_mask_filters,
            maskPage: MASK_PAGE,
            appendFilter: appendLiteralFieldTrimPagedFilter,
        });
        return extra;
    }, [pageSizePc, getTable, state.user_mask_filters]);

    // Pusher: maintenance_template_record – tabuľka je pre jeden template, reagujeme len ak maintenance_template_id === formTemplate.id
    useEffect(() => {
      const channel = getPusherChannel();
      if (!channel || formTemplate?.id == null) return;
      const currentTemplateId = formTemplate.id;
      const DEDUPE_MS = 800;
      const lastHandled = { key: '', at: 0 };
      const shouldSkip = (eventType, id) => {
        if (id == null) return true;
        const key = `${eventType}-${id}`;
        const now = Date.now();
        if (lastHandled.key === key && now - lastHandled.at < DEDUPE_MS) return true;
        lastHandled.key = key;
        lastHandled.at = now;
        return false;
      };
      const onCreated = (data) => {
        if (data?.maintenance_template_id != null && Number(data.maintenance_template_id) !== Number(currentTemplateId)) return;
        const id = data?.id ?? null;
        if (id == null || shouldSkip('maintenance_template_record.created', id)) return;
        getRequest(`/api/maintenance_template_records/${id}`).then((res) => {
          const rowData = res?.data ?? res;
          if (!rowData?.id || Number(rowData.maintenance_template_id) !== Number(currentTemplateId)) return;
          const table = getTable();
          const row = table?.getRow?.(rowData.id);
          if (row) row.update(rowData);
          else table?.addData?.([rowData], true);
        }).catch(() => {});
      };
      const onUpdated = (data) => {
        if (data?.maintenance_template_id != null && Number(data.maintenance_template_id) !== Number(currentTemplateId)) return;
        const id = data?.id ?? null;
        if (id == null || shouldSkip('maintenance_template_record.updated', id)) return;
        getRequest(`/api/maintenance_template_records/${id}`).then((res) => {
          const rowData = res?.data ?? res;
          if (!rowData?.id || Number(rowData.maintenance_template_id) !== Number(currentTemplateId)) return;
          const table = getTable();
          const row = table?.getRow?.(rowData.id);
          if (row) row.update(rowData);
          else table?.addData?.([rowData], true);
        }).catch(() => {});
      };
      const onDeleted = (data) => {
        if (data?.maintenance_template_id != null && Number(data.maintenance_template_id) !== Number(currentTemplateId)) return;
        const id = data?.id ?? null;
        if (id == null || shouldSkip('maintenance_template_record.deleted', id)) return;
        const table = getTable();
        const row = table?.getRow?.(id) ?? table?.getRows?.().find((r) => r.getData().id === id);
        if (row) row.delete();
      };
      channel.bind('maintenance_template_record.created', onCreated);
      channel.bind('maintenance_template_record.updated', onUpdated);
      channel.bind('maintenance_template_record.deleted', onDeleted);
      return () => {
        channel.unbind('maintenance_template_record.created', onCreated);
        channel.unbind('maintenance_template_record.updated', onUpdated);
        channel.unbind('maintenance_template_record.deleted', onDeleted);
      };
    }, [getTable, formTemplate?.id]);

    const ajaxRequestFunc = useMemo(
        () => createDedupedAjaxRequest(buildRequestParams, tabulatorGet, inFlightRef, completedCacheRef),
        [buildRequestParams]
    );

    const [machineType,setMachineType] = useState([]);//training department
    const [maintenanceTypes,setMaintenanceTypes] = useState([]);//training

    const [columnsVisible, setColumnsVisible] = useState(() => ({ ...DEFAULT_MAINTENANCE_TEMPLATE_RECORD_ADD_COLUMN_VISIBILITY }));
    const [maskVisible, setMaskVisible] = useState(false);

const [formType, setFormType] = useState(type);
    const [localTemplates, setLocalTemplates] = useState([]);

    useEffect(() => {
        if (Array.isArray(maintenanceTemplates) && maintenanceTemplates.length > 0) {
            setLocalTemplates(maintenanceTemplates);
        }
    }, [maintenanceTemplates]);

    //TODO dorobit contractor id
    const [insertCopyIcon,setInsertCopyIcon] = useState((user.permissions?.includes("add maintenance"))?false:true);
    const [scrollIcons,setScrollIcons] = useState((user.permissions?.includes("edit maintenance"))?false:true);
    const [visibleCard,setVisibleCard] = useState(false);
    const [editIcon,setEditIcon] = useState(true);
    const [deleteIcon,setDeleteIcon] = useState(true);
    const [copyIcon,setCopyIcon] = useState(true);

    const [selectedCard,setSelectedCard] = useState(null);

    const [showTemplateRecordAdd, setShowTemplateRecordAdd] = useState(false);

    function addNewMaintenanceCard(){
        setMachineType([]);
        setMaintenanceTypes(allMaintenanceTypes || []);
        setSelectedCard(null);
        setShowTemplateRecordAdd(true);
    }

    function editNewMaintenanceCard() {
        const selected = getTable()?.getSelectedData?.()?.[0];
        if (selected) {
            formikUpdate.setValues(selected);
            filterMaintenanceTypes(selected.maintenance_type?.machine_type_id, selected.maintenance_type);
            setVisibleCard(true);
        }
    }

    function hideEditCard(){
        formikUpdate.resetForm();
        setVisibleCard(false);
    }

    const onSelectRow = () => {
        setEditIcon(true);
        setCopyIcon(true);
        setDeleteIcon(true);
        const selected = getTable()?.getSelectedData?.() ?? [];
        if (selected.length === 1) {
            if (user.permissions?.includes("edit maintenance")) setEditIcon(false);
            if (user.permissions?.includes("delete maintenance")) setDeleteIcon(false);
            if (user.permissions?.includes("add maintenance")) setCopyIcon(false);
            setSelectedCard(selected[0]);
        }
    }

    function filterMaintenanceTypes(machineTypeId, maintenance_type = null) {
        setMachineType(machineTypeId);
        const types = allMaintenanceTypes || [];
        const mtId = machineTypeId === "" || machineTypeId == null ? null : parseInt(machineTypeId, 10);
        let list;
        if (mtId == null) {
            list = types;
        } else {
            list = types.filter((m) => {
                const typeId = Number(m.machine_type?.id ?? m.machine_type_id);
                return typeId === mtId;
            });
        }
        if (maintenance_type?.id && !list.some((m) => Number(m.id) === Number(maintenance_type.id))) {
            setMaintenanceTypes([...list, maintenance_type]);
            return;
        }
        setMaintenanceTypes(list);
    }

    const getTemplateRecordsForCurrentTemplate = () => {
        const template = (localTemplates || []).find((t) => t.id == formTemplate?.id) ?? formTemplate;
        return template?.template_records ?? [];
    };

    const existsDuplicateCombo = (values, excludeId = null) => {
        const typeId = String(values?.maintenance_type_id ?? "");
        const part = normalize((values?.machine_part ?? "").toString());
        if (!typeId) return false;
        const rows = getTemplateRecordsForCurrentTemplate();
        return rows.some((r) => {
            if (excludeId != null && String(r.id) === String(excludeId)) return false;
            return String(r.maintenance_type_id) === typeId && normalize(String(r.machine_part ?? "")) === part;
        });
    };

    const validate = values => {
        const errors = {};
        if (existsDuplicateCombo(values, null)) {
            errors.machine_part = t('Maintenance.Toast.TaskExistsForPart');
        }
        return errors;
    };

    const validateUpdate = values => {
        const errors = {};
        if (existsDuplicateCombo(values, values.id)) {
            errors.machine_part = t('Maintenance.Toast.TaskExistsForPart');
        }
        return errors;
    };

    const formik = useFormik({
        initialValues:{
            maintenance_type_id:'',required_attachment:0,machine_part:'',responsible_employee_id:''
        },
        enableReinitialize:true,
        validate,
        onSubmit: values => {
            setDialogText(t('Common.Saving'));
            setVisible(true);
            let formData = new FormData();
            for (let value in values) {
                formData.append(value, values[value] != null ? values[value] : "");
            }
            formData.append("maintenance_template_id",formTemplate.id);
            const mtAdd = getSelectedMaintenanceType(values.maintenance_type_id, null, maintenanceTypes, allMaintenanceTypes);
            if (mtAdd?.use_central_required_attachment) formData.set('required_attachment', '1');
            postRequest('/api/maintenance_template_records', formData,true)
            .then(() => {
                    captureHScroll();
                    refreshFromStart();
                    getRequest("/api/maintenance_templates").then((r) => {
                        const data = r?.data ?? [];
                        setLocalTemplates(data);
                        const updated = data.find(t => t.id === formTemplate.id);
                        if (updated) setFormTemplate(updated);
                    });
                    if(closeTab)dispatch({type:'close_tab',id:state.selectedTab});
                    formik.resetForm();
                    setVisible(false);
                    toast?.current?.show({ severity: 'info', summary: t('Common.Confirmed'), life: 3000, detail: t('Common.Toast.RecordSavedDetail') });
                    setShowTemplateRecordAdd(false);
                })
            .catch((reason) => {
                setVisible(false);
                if(!navigator.online){
                    alert(t('ServiceTask.OfflineQueuedAlert'));
                }
                else{
                    alert(t('Common.Toast.ActionFailedDetail'));
                }
            });
        },
    });

    const formikUpdate = useFormik({
        initialValues: selectedCard == null ? {
            maintenance_type_id:'',required_attachment:0,machine_part:'',responsible_employee_id:''
        } : selectedCard,
        enableReinitialize:true,
        validate: validateUpdate,
        onSubmit: values => {
            setDialogText(t('Common.Saving'));
            setVisible(true);
            let formData = new FormData();
            for (let value in values) {
                formData.append(value, values[value] != null ? values[value] : "");
            }
            formData.append("maintenance_template_id",formTemplate.id);
            const mtUpdate = selectedCard?.maintenance_type ?? getSelectedMaintenanceType(values.maintenance_type_id, null, maintenanceTypes, allMaintenanceTypes);
            if (mtUpdate?.use_central_required_attachment) formData.set('required_attachment', '1');
            postRequest(`/api/maintenance_template_records/update/${formikUpdate.values.id}`, formData,true)
            .then(() => {
                    captureHScroll();
                    refreshFromStart();
                    getRequest("/api/maintenance_templates").then((r) => {
                        const data = r?.data ?? [];
                        setLocalTemplates(data);
                        const updated = data.find(t => t.id === formTemplate.id);
                        if (updated) setFormTemplate(updated);
                    });
                    if(closeTab)dispatch({type:'close_tab',id:state.selectedTab});
                    setVisibleCard(false);
                    formikUpdate.resetForm();
                    setVisible(false);
                    toast?.current?.show({ severity: 'info', summary: t('Common.Confirmed'), life: 3000, detail: t('Common.Toast.RecordSavedDetail') });
                })
            .catch((reason) => {
                setVisible(false);
                if(!navigator.online){
                    alert(t('ServiceTask.OfflineQueuedAlert'));
                }
                else{
                    alert(t('Common.Toast.ActionFailedDetail'));
                }
            });
        },
    });

    const resetEmbeddedRecordUi = useCallback(() => {
        setVisibleCard(false);
        setShowTemplateRecordAdd(false);
        setTemplatePreview(null);
        setSelectedCard(null);
        setEditIcon(true);
        setDeleteIcon(true);
        setCopyIcon(true);
        formik.resetForm();
        formikUpdate.resetForm();
    }, [formik, formikUpdate]);

    useEffect(() => {
        const prevId = prevTemplateIdRef.current;
        const nextId = formTemplate?.id;
        if (prevId === nextId) return;
        prevTemplateIdRef.current = nextId;
        if (nextId == null || !tableBuiltRef.current) return;
        inFlightRef.current = { sig: null, promise: null };
        completedCacheRef.current.clear();
        captureHScroll();
        refreshFromStart();
    }, [formTemplate?.id, captureHScroll, refreshFromStart]);

    const { scrollToFirst, scrollUp, scrollDown, scrollToLast } = useMemo(() => {
        const applyTemplate = (data) => {
            if (!data) return;
            resetEmbeddedRecordUi();
            currentTemplateIdRef.current = data.id ?? null;
            setFormTemplate(data);
        };
        const buildFetchRecord = (toParams = () => '') => async (direction, currentId) => {
            setDialogText?.(t('Common.Loading'));
            setVisible?.(true);
            try {
                const q = toParams();
                const url = `/api/maintenance_templates/navigate?direction=${direction}${currentId ? '&id=' + currentId : ''}${q ? '&' + q : ''}`;
                const resp = await getRequest(url);
                return resp?.data ?? null;
            } finally {
                setVisible?.(false);
            }
        };
        if (navigationContext?.filters != null || navigationContext?.sorters != null) {
            const toParams = () => navigationContextToQueryParams(navigationContext);
            return createAsyncNavigateHandlers(buildFetchRecord(toParams), applyTemplate, () => currentTemplateIdRef.current ?? formTemplate?.id);
        }
        return createAsyncNavigateHandlers(buildFetchRecord(), applyTemplate, () => currentTemplateIdRef.current ?? formTemplate?.id);
    }, [navigationContext, formTemplate?.id, resetEmbeddedRecordUi, setDialogText, setVisible]);

    function onEditRow(template_record){
        formikUpdate.setValues(template_record);
        setSelectedCard(template_record);
        filterMaintenanceTypes(template_record.maintenance_type.machine_type_id,template_record.maintenance_type);
        setVisibleCard(true);
    };

    useEffect(() => {
        if (!visibleCard) {
            setTemplatePreview(null);
            return;
        }
        const typeId = formikUpdate.values?.maintenance_type_id ?? selectedCard?.maintenance_type_id;
        if (!typeId) return;
        getRequest(`/api/maintenance_types/${typeId}/template`)
            .then((res) => {
                const d = res?.data?.data ?? res?.data;
                if (d?.url) setTemplatePreview({ url: d.url, filename: d.filename || 'predloha' });
                else setTemplatePreview(null);
            })
            .catch(() => setTemplatePreview(null));
    }, [visibleCard, formikUpdate.values?.maintenance_type_id, selectedCard?.maintenance_type_id]);

    function EditRowFormatter(props) {
        const rowData = props.cell.getRow().getData();
        return <div className='flex items-center justify-center w-full h-full'><button type='button' onClick={() => onEditRow(rowData)}><EditRowIcon/></button></div>;
    }

    const reject = () => {
        toast?.current?.show({ severity: 'warn', summary: t('Common.Rejected'), detail: t('Common.Toast.RequestCancelled'), life: 3000 });
    }

    const accepttraining = () => {
        let rows_to_delete = [];
        rows_to_delete = getTable()?.getSelectedData?.() ?? [];
        rows_to_delete.forEach(element => {
          console.log(element);
          deleteRequest(`/api/maintenance_template_records/${element.id}`)
              .then(() => {
                toast?.current?.show({ severity: 'info', summary: t('Common.Confirmed'), life: 3000, detail: t('Common.Toast.RecordDeletedDetail') });
                captureHScroll();
                refreshFromStart();
              }).catch((reason) => {
                  alert(reason);
                  toast?.current?.show({ severity: 'warn', summary: t('Common.Rejected'), detail: t('Common.Toast.DeleteFailedDetail'), life: 3000 });
              })
        });
    }

    const confirm = () => {
        confirmDialog({
            message: t('Common.ConfirmDeleteRecord'),
            header: t('Common.ConfirmDelete'),
            icon: 'pi pi-info-circle',
            acceptClassName: 'p-button-danger',
            acceptLabel: t('Common.No'),
            rejectLabel: t('Common.Yes'),
            accept: reject,
            reject: accepttraining
        });
    };

    function normalize(str) {
        return (str ?? "").toString().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
    }

    function textHeaderFilter(headerValue, rowValue, rowData, filterParams) {
        if (!headerValue) return true; // prázdny filter = všetko
        return normalize(rowValue).includes(normalize(headerValue));
      }

    function RequiredAttachmentFormatter(props) {
        const rowData = props.cell.getRow().getData();
        const raw = rowData?.required_attachment;
        const value = Number(raw) === 1 ? 1 : 0;
        return (
            <div className="w-full">
            {value === 1 ? t('Common.Yes') : t('Common.No')}
            </div>
        );
    }

    /** Efektívna zodpovedná: z typu pri use_central_responsible_person, inak z záznamu (aby zoznam súhlasil s dialógom). */
    function EffectiveResponsibleFormatter(props) {
        const rowData = props.cell.getRow().getData();
        const mt = rowData?.maintenance_type;
        const name = mt?.use_central_responsible_person
            ? (mt?.responsible?.name ?? '')
            : (rowData?.responsible?.name ?? '');
        return <span>{name || '—'}</span>;
    }
    function effectiveResponsibleHeaderFilter(headerValue, rowValue, rowData) {
        if (!headerValue) return true;
        const mt = rowData?.maintenance_type;
        const name = mt?.use_central_responsible_person
            ? (mt?.responsible?.name ?? '')
            : (rowData?.responsible?.name ?? '');
        return normalize(name).includes(normalize(headerValue));
    }
      
    const dateFormatGlob = getDateFormat?.(state?.module_settings) ?? 'dd.MM.yyyy';
    const dateTimeFormatGlob = `${dateFormatGlob} HH:mm`;

    const auditColumns = useMemo(
        () =>
          buildAuditTabulatorColumns({
            includeId: true,
            dateFormat: dateFormatGlob,
            dateTimeFormat: dateTimeFormatGlob,
            getUserColor,
            getUserInitials,
            textHeaderFilter,
          }),
        [dateFormatGlob, dateTimeFormatGlob, getUserColor, getUserInitials, textHeaderFilter]
      );
      
      const idColumn = useMemo(
        () => auditColumns.find((column) => column.field === 'id'),
        [auditColumns]
      );
      
      const createdAtColumn = useMemo(
        () => auditColumns.find((column) => column.field === 'created_at'),
        [auditColumns]
      );
      
      const updatedAtColumn = useMemo(
        () => auditColumns.find((column) => column.field === 'updated_at'),
        [auditColumns]
      );
      
      const canSetStandardMask = user?.permissions?.includes('set standard mask');
      
      const columns = useMemo(() => [
        { title: '', field: 'rowSelection', formatter:"rowSelection", titleFormatter:"rowSelection", hozAlign:"center", width: 44, headerSort:false, resizable: false, frozen: true, print: false, download: false },
        { title: '', field: 'editor', hozAlign:"center", width: 44, headerSort:false, formatter: reactFormatter(<EditRowFormatter/>), resizable:false, frozen: true, print: false, download: false },
      
        idColumn,
      
        { title: t('Common.TaskName'), field: 'maintenance_type.name', minWidth: 200, widthGrow:3, headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc:textHeaderFilter},
        { title: t('Common.TaskType'), field: 'maintenance_type.type', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, minWidth: 200, headerFilterFunc:textHeaderFilter },
        { title: t('Common.Norm'), field: 'maintenance_type.norm', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, minWidth: 200, headerFilterFunc:textHeaderFilter },
        { title: t('MachineList.Column.MachineTypeName'), field: 'maintenance_type.machine_type.name', minWidth: 200, headerFilter: primeMultiSelectHeaderFilter, headerFilterParams: { placeholder: t('MachineList.Column.MachineTypeName'), valueType: 'string', options: (state.machine_types || []).map(mt => ({ label: mt.name, value: mt.name })) } },
        { title: 'Interval (v mesiacoch)', field: 'maintenance_type.interval_months', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, minWidth: 200, headerFilterFunc:textHeaderFilter },
        { title: t('Common.Column.Responsible'), field: 'responsible.name', minWidth: 200, formatter: reactFormatter(<EffectiveResponsibleFormatter />), headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, headerFilterFunc: effectiveResponsibleHeaderFilter },
        { title: t('Common.Description'), field: 'maintenance_type.description', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, minWidth: 200, headerFilterFunc:textHeaderFilter },
        { title: t('Common.RequiredAttachment'), field: 'required_attachment', minWidth: 200, formatter: reactFormatter(<RequiredAttachmentFormatter />), headerFilter: primeMultiSelectHeaderFilter, headerFilterParams: { placeholder: t('Common.RequiredAttachment'), options: [{ label: t('Common.Yes'), value: '1' }, { label: t('Common.No'), value: '0' }] } },
        { title: t('Common.MachinePart'), field: 'machine_part', headerFilter: inputOnEnterHeaderFilter, headerFilterLiveFilter: false, minWidth: 200, headerFilterFunc:textHeaderFilter },
      
        createdAtColumn,
        updatedAtColumn,
      ].filter(Boolean), [
        idColumn,
        createdAtColumn,
        updatedAtColumn,
        state.machine_types,
        textHeaderFilter,
        effectiveResponsibleHeaderFilter,
      ]);

      const [expandedKeys, setExpandedKeys] = useState({});

      const closeMenu = useCallback(() => {
        setTimeout(() => {
          OverflowMenuProvider.setVisible?.(null);
          setExpandedKeys({});
        }, 0);
      }, []);

      const hasApplicableStandardMask = useMemo(
        () => hasApplicableStandardMaskRow(state.standard_mask_filters, MASK_PAGE, mergeMaintenanceTemplateRecordAddColumnVisibilityFromSaved),
        [state.standard_mask_filters]
      );

      const openMaskDialog = useCallback(
        createOpenMaskDialog({
          columnsVisible,
          setColumnsVisible,
          defaultColumnVisibility: DEFAULT_MAINTENANCE_TEMPLATE_RECORD_ADD_COLUMN_VISIBILITY,
          mergeVisibilityFromSaved: mergeMaintenanceTemplateRecordAddColumnVisibilityFromSaved,
          setMaskVisible,
          closeMenu,
        }),
        [columnsVisible, closeMenu]
      );

      const setMask = useCallback(
        createMaskSetFieldHandler({
          defaultColumnVisibility: DEFAULT_MAINTENANCE_TEMPLATE_RECORD_ADD_COLUMN_VISIBILITY,
          columnsVisible,
          setColumnsVisible,
          getTable,
          applyVisibilityToTable: applyMaintenanceTemplateRecordAddDataColumnVisibility,
          toast,
        }),
        [columnsVisible, getTable, toast]
      );

      const maskSelectAll = useCallback(
        createMaskSelectAllHandler({
          defaultColumnVisibility: DEFAULT_MAINTENANCE_TEMPLATE_RECORD_ADD_COLUMN_VISIBILITY,
          setColumnsVisible,
          getTable,
          applyVisibilityToTable: applyMaintenanceTemplateRecordAddDataColumnVisibility,
        }),
        [getTable]
      );

      const maskDeselectAll = useCallback(
        createMaskDeselectAllHandler({
          defaultColumnVisibility: DEFAULT_MAINTENANCE_TEMPLATE_RECORD_ADD_COLUMN_VISIBILITY,
          setColumnsVisible,
          getTable,
          applyVisibilityToTable: applyMaintenanceTemplateRecordAddDataColumnVisibility,
        }),
        [getTable]
      );

      const saveUserMaskColumns = useCallback(
        createSaveUserMaskColumns({
          maskPage: MASK_PAGE,
          getTable,
          setUserMaskFilter,
          defaultColumnVisibility: DEFAULT_MAINTENANCE_TEMPLATE_RECORD_ADD_COLUMN_VISIBILITY,
          columnsVisible,
          setColumnsVisible,
          setMaskVisible,
          toast,
        }),
        [columnsVisible, getTable, setUserMaskFilter, toast]
      );

      const confirmSaveStandardMask = useCallback(
        createConfirmSaveStandardMask({
          maskPage: MASK_PAGE,
          getTable,
          setStandardMaskFilter,
          defaultColumnVisibility: DEFAULT_MAINTENANCE_TEMPLATE_RECORD_ADD_COLUMN_VISIBILITY,
          toast,
        }),
        [getTable, setStandardMaskFilter, toast]
      );

      const confirmApplyStandardLayoutToUserMask = useCallback(
        createConfirmApplyStandardLayoutToUserMask({
          maskPage: MASK_PAGE,
          state,
          setUserMaskFilter,
          defaultColumnVisibility: DEFAULT_MAINTENANCE_TEMPLATE_RECORD_ADD_COLUMN_VISIBILITY,
          mergeVisibilityFromSaved: mergeMaintenanceTemplateRecordAddColumnVisibilityFromSaved,
          toast,
          closeMenu,
          onAppliedToTable: ({ colsForSave, attributes }) => {
            const table = getTable();
            if (!table) return;
            isRestoringRef.current = true;
            try {
              table.setColumnLayout?.(attributes);
            } catch (_) {}
            setColumnsVisible({ ...colsForSave, id: true });
            applyMaintenanceTemplateRecordAddDataColumnVisibility(table, { ...colsForSave, id: true });
            setTimeout(() => {
              isRestoringRef.current = false;
              table.redraw?.(true);
              captureHScroll();
              refreshFromStart();
            }, 0);
          },
        }),
        [state, closeMenu, getTable, setUserMaskFilter, toast, captureHScroll, refreshFromStart]
      );

      const itemRenderer = (item, options) => (
        <a className="flex align-items-center px-3 py-2 cursor-pointer items-center bg-[#333] text-white w-[180px]" onClick={options.onClick}>
            <span className={`${item.icon} text-primary`} />
            <span className={`mx-2 ${item.items && 'font-semibold'}`}>{item.label}</span>
            {item.shortcut && <span className="ml-auto border-1 surface-border border-round surface-100 text-xs p-1">{item.shortcut}</span>}
        </a>
      );

      const handleClickOutside = (event) => {
        if (event.target.closest('[data-overflow-trigger]')) return;
        if (overlayRef.current && !overlayRef.current.contains(event.target)) {
          setExpandedKeys({});
          OverflowMenuProvider.setVisible?.(null);
        }
      };

      useEffect(() => {
        document.addEventListener('click', handleClickOutside, true);
        return () => document.removeEventListener('click', handleClickOutside, true);
      }, []);

      const items = [
        {
          key: '0',
          label: t('Common.Actions'),
          icon: 'pi pi-sliders-v',
          template: itemRenderer,
          items: buildMaintenanceMaskAkcieSubItems({
            maskPage: MASK_PAGE,
            getTable,
            setUserMaskFilter,
            cleanSorters,
            captureHScroll,
            refreshFromStart,
            closeMenu,
            openMaskDialog,
            openChosenNotificationPreferences,
            hasApplicableStandardMask,
            confirmApplyStandardLayoutToUserMask,
            itemRenderer,
          }),
        },
      ];

    function getSelectedMaintenanceType(typeId, fallbackObj, maintenanceTypes, allTypes) {
        const id = Number(typeId);
        if (!id) return null;

        // pri update často máš už priamo objekt v selectedCard.maintenance_type
        if (fallbackObj?.id === id) return fallbackObj;

        return (
            maintenanceTypes?.find((m) => Number(m.id) === id) ||
            allTypes?.find((m) => Number(m.id) === id) ||
            null
        );
    }

    /** Text option pre select úkonu: Názov úkonu — norma — interval (ako mntn017) */
    function maintenanceTypeOptionLabel(mt) {
        if (!mt) return '';
        const n = mt.name ?? '';
        const norm = mt.norm ? ` — ${mt.norm}` : '';
        const interval = mt.interval_months != null ? ` — ${mt.interval_months}mes.` : '';
        return `${n}${norm}${interval}`.trim();
    }

    function MaintenanceTypeDetails({ mt }) {
        if (!mt) return null;
        return (
            <div className="mt-2 p-3 rounded-md border bg-gray-50 text-sm">
                <div className="font-semibold text-lg mb-2">Detaily úkonu</div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                    <div className="text-gray-600 text-lg">Názov</div>
                    <div className="font-medium">{mt?.name || "-"}</div>
                    <div className="text-gray-600 text-lg">Druh úkonu</div>
                    <div>{mt?.type || "-"}</div>
                    <div className="text-gray-600 text-lg">Norma</div>
                    <div>{mt?.norm || "-"}</div>
                    <div className="text-gray-600 text-lg">{t('MachineList.Column.MachineTypeName')}</div>
                    <div>{mt?.machine_type?.name || "-"}</div>
                    <div className="text-gray-600 text-lg">{t('Common.IntervalMonths')}</div>
                    <div>{mt?.interval_months ?? "-"}</div>
                    <div className="text-gray-600 text-lg">Zodpovedná osoba</div>
                    <div>{mt?.responsible?.name || "-"}</div>
                    <div className="text-gray-600 text-lg">Popis</div>
                    <div>{mt?.description || "-"}</div>
                </div>
            </div>
        );
    }

    const selectedMtAdd = getSelectedMaintenanceType(
        formik.values.maintenance_type_id,
        null,
        maintenanceTypes,
        allMaintenanceTypes
    );

    const selectedMtUpdate = getSelectedMaintenanceType(
        formikUpdate.values.maintenance_type_id,
        formikUpdate.values?.maintenance_type,
        maintenanceTypes,
        allMaintenanceTypes
    );

    function getResponsibleEmployeeIdFromMaintenanceType(mt) {
        if (!mt || !mt.use_central_responsible_person) return "";
        if (mt.responsible_employee_id != null) return mt.responsible_employee_id;
        if (mt.responsible?.id != null) return mt.responsible.id;
        return "";
    }

    return ( 
        <>
            <div>
            <div className='w-full h-10 flex flex-row bg-[#333]'>
            <IntersectionObserverWrapper>
            <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="add"><button className="disabled:opacity-25" disabled={insertCopyIcon} onClick={() => addNewMaintenanceCard()}><AddIcon/></button></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="edit"><button className="disabled:opacity-25" disabled={editIcon} onClick={() => editNewMaintenanceCard()}><EditIcon/></button></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="delete"><button className="disabled:opacity-25" disabled={deleteIcon} onClick={confirm}><DeleteIcon/></button></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="refresh"><button type="button" onClick={() => { captureHScroll(); refreshFromStart(); }} title={t('Common.Refresh')}><RefreshIcon /></button></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="vert"><VerticalLineIcon/></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="first"><button className="disabled:opacity-25" onClick={() => scrollToFirst()} disabled={scrollIcons}><BackToFirstIcon/></button></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="back"><button className="disabled:opacity-25" onClick={() => scrollUp()} disabled={scrollIcons}><BackIcon/></button></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="forward"><button className="disabled:opacity-25" onClick={() => scrollDown()} disabled={scrollIcons}><ForwardIcon/></button></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="last"><button className="disabled:opacity-25" onClick={() => scrollToLast()} disabled={scrollIcons}><ForwardToLastIcon/></button></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="vert"><VerticalLineIcon/></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="favorite"><button className="disabled:opacity-25" disabled onClick={() => openFavoriteDialog(t('Print.Title.MaintenanceTemplateRecordAdd'),"MaintenanceTemplateRecordAdd")}><FavoriteIcon/></button></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="price"><button className="disabled:opacity-25" disabled onClick={() => setFastView("MaintenanceTemplateRecordAdd",columnsVisible,columns)}><PriceIcon/></button></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="vert"><VerticalLineIcon/></div>
          {canSetStandardMask ? (
            <div className="flex w-10 h-10 px-2 py-1 justify-center" data-targetid="save-standard-mask">
              <button
                type="button"
                title={t('Common.SaveStandardMask')}
                aria-label={t('Common.SaveStandardMask')}
                onClick={confirmSaveStandardMask}
                className="flex items-center justify-center"
              >
                <i className="pi pi-bookmark text-white text-xl" aria-hidden="true" />
              </button>
            </div>
          ) : null}
          <div className="card flex justify-content-center z-[100]" data-targetid="actions" ref={overlayRef}>
            <PanelMenu model={items} expandedKeys={expandedKeys} onExpandedKeysChange={setExpandedKeys} className="w-full md:w-20rem"/>
          </div>
          </IntersectionObserverWrapper>
        </div>
        <Splitter className='h-[calc(100vh_-_177px)]' layout="vertical" pt={{ gutter: { className: 'bg-[#333] h-8' },gutterHandler: { className: 'bg-secondary ' } }}>
        <SplitterPanel className="flex align-items-center justify-content-center overflow-y-auto " minSize={5} size={5}>
        <form onSubmit={formik.handleSubmit} className='w-full'>
        <div className="relative flex flex-col overflow-x">
          <div className='xl:grid grid-cols-2 gap-1 mb-2'>
          <div className="w-auto p-6 m-4 bg-gray-100 rounded-md shadow-md">
          <h1 className="text-3xl font-semibold text-left text-zinc-900">{t('Ui.R67.UKONY_K_SABLONE')}</h1>
                {formType=="edit" && <label className="block text-md font-semibold text-gray-800">Por.č.: {formTemplate.id}</label>}
                <div>
                    <label
                        htmlFor="name"
                        className="block text-md font-semibold text-gray-800"
                    >{t('ServiceTaskPlanner.TemplateNameLabel')}</label>
                    <input 
                        name='name'
                        type="text" readOnly
                        value={formTemplate.name}
                        className="block w-full  px-4 py-2 mt-2 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                    />
                </div>     
                <button className='hidden' type='submit' ref={submitRef}></button> 
            </div>
            <div className="w-auto p-6 m-4 bg-gray-100 rounded-md shadow-md">
                <div className="mt-6">
                    <div className="md:grid grid-cols-2 gap-4 mb-2">
                        <div>
                            <label
                                htmlFor="machine_type_id"
                                className="block text-md font-semibold text-gray-800"
                            >{t('MachineList.Column.MachineTypeName')}</label>
                            <select
                            name='machine_type_id' disabled
                            value={formTemplate.machine_type_id}
                            className='block w-full px-4 py-2 mt-2 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40'>
                            <option value="">{t('Ui.R67.Vyber_druh')}</option>
                            {state.machine_types.map((machine_type) => (
                                <option key={machine_type.id} value={machine_type.id}>{machine_type.name}</option>
                            ))}
                            </select>
                        </div>
                        <div>
                        <label
                            htmlFor="description"
                            className="block text-md font-semibold text-gray-800"
                        >{t('Common.Description')}</label>
                        <textarea
                            name='description'
                            type="text" readOnly
                            value={formTemplate.description}
                            className="block w-full  px-4 py-2 mt-2 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                        >
                        </textarea>
                        </div>
                    </div>
                </div>  
            </div>
            </div> 
            </div>
             </form>
        </SplitterPanel>
        <SplitterPanel className="flex flex-col align-items-center justify-content-center w-full overflow-y-auto" size={40} style={{minHeight: '110px'}}>
            <ReactTabulator
            data={undefined}
            onRef={(r) => { ref.current = r?.current; }}
            className='h-full w-full'
            columns={columns}
            events={{
                rowSelectionChanged: onSelectRow,
                tableBuilt: () => {
                    const table = getTable();
                    if (!table) return;
                    const effective = getEffectiveMask(MASK_PAGE, state);
                    if (effective?.type === 'user' && effective.userMask) {
                        isRestoringRef.current = true;
                        applyUserMaskRowToTabulator(table, effective.userMask, {
                            setColumnsVisible,
                            clearHeaderFiltersWhenEmpty: true,
                            applyColumnVisibilityFn: (tbl, jc, setVis) => {
                                const merged = mergeMaintenanceTemplateRecordAddMaskDialogVisibility(jc);
                                setVis(merged);
                                applyMaintenanceTemplateRecordAddDataColumnVisibility(tbl, merged);
                            },
                        });
                        setTimeout(() => { isRestoringRef.current = false; }, 0);
                    } else if (effective?.type === 'standard' && effective.standardMask) {
                        isRestoringRef.current = true;
                        const m = effective.standardMask;
                        const attributes = safeParse(m.attributes, []);
                        const jsonColumns = safeParse(m.columns, {});
                        if (Array.isArray(attributes) && attributes.length > 1) {
                            table.setColumnLayout?.(attributes);
                        }
                        if (jsonColumns && typeof jsonColumns === 'object') {
                            const merged = mergeMaintenanceTemplateRecordAddMaskDialogVisibility(jsonColumns);
                            setColumnsVisible(merged);
                            applyMaintenanceTemplateRecordAddDataColumnVisibility(table, merged);
                        }
                        setTimeout(() => { isRestoringRef.current = false; }, 0);
                    }
                    tableBuiltRef.current = true;
                    if (effective?.type === 'user') refreshFromStart();
                },
                ...getTabulatorPersistLayoutEvents(MASK_PAGE, ref, setUserMaskFilter),
                headerFilterChanged: () => { if (!isRestoringRef.current) captureHScroll(); },
                sortChanged: () => { if (!isRestoringRef.current) captureHScroll(); },
                renderComplete: () => restoreHScroll(),
            }}
            options={{
                renderHorizontal: "virtual",
                rowHeight: 42,
                movableColumns: true,
                printAsHtml:true,printHeader: () => buildCanonicalPrintHeaderHtml({
                      branding: state?.branding,
                      productFallbackSrc: defaultProductLogoSrc,
                      companySettingsPrint: state?.company_settings_print,
                      moduleSettings: state?.module_settings,
                      title: t('Print.Title.MaintenanceTemplateRecordAdd'),
                      printDateLabel: t('Print.DateLabel'),
                    }),
                    printFooter: () => buildCanonicalPrintFooterHtml({
                      companySettingsPrint: state?.company_settings_print,
                    }),
                printRowRange: "active",
                initialSort: [{ column: "id", dir: "asc" }],
                ajaxURL: "/api/maintenance_template_records/paged",
                ajaxRequestFunc,
                filterMode: "remote",
                sortMode: "remote",
                progressiveLoad: "scroll",
                progressiveLoadScrollMargin: 80,
                paginationSize: pageSizePc,
                pagination: false,
                dataLoader: true,
                dataLoaderLoading: t('Common.LoadingEllipsis'),
                dataLoaderError: t('Common.LoadFailed'),
                ajaxRequesting: () => {
                    captureHScroll();
                    const table = getTable();
                    if (table?.initialized) table.alert?.(t('Common.LoadingEllipsis'));
                    return true;
                },
                ajaxResponse: (_url, _params, response) => {
                    getTable()?.clearAlert?.();
                    return response;
                },
                ajaxError: () => {
                    getTable()?.clearAlert?.();
                },
            }}/>
        </SplitterPanel>
    </Splitter> 
    <Dialog header={t('MaintenanceTemplateRecordAdd.Text.Upravit_v_sablone')} visible={visibleCard} style={{ width: '40vw' }} onHide={() => hideEditCard()}>
        <form onSubmit={formikUpdate.handleSubmit}>
            <div>
                <label
                    htmlFor="machineType"
                    className="block text-md font-semibold text-gray-800"
                >{t('MachineList.Column.MachineTypeName')}</label>
                <select 
                required
                name='machineType'
                onChange={(e) => filterMaintenanceTypes(e.target.value)} value={machineType}
                className="bg-white block w-full  px-4 py-2 mt-2 text-grey-700 border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40">
                <option value="">{t('ServiceTaskDetail.SelectDepartment')}</option>
                {state.machine_types?.map((machine_type) => (
                    <option key={machine_type.id} value={machine_type.id}>{machine_type.name}</option>
                    ))}
                </select>
            </div>
            <div className='mt-3'>
                <label
                    htmlFor="maintenance_type_id"
                    className="block text-md font-semibold text-gray-800"
                >{t('Common.TaskName')}</label>
                <select required
                name='maintenance_type_id'
                onChange={(e) => {
                    const newId = e.target.value;
                    formikUpdate.setFieldValue("maintenance_type_id", newId);
                    const mt = getSelectedMaintenanceType(newId,null,maintenanceTypes,allMaintenanceTypes);
                    const respId = getResponsibleEmployeeIdFromMaintenanceType(mt);
                    formikUpdate.setFieldValue("responsible_employee_id", respId || "");
                }} 
                value={formikUpdate.values.maintenance_type_id}
                className='block w-full px-4 py-2 mt-2 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40'>
                <option value="">{t('Ui.R67.Vyber_ukon')}</option>
                {maintenanceTypes.map((mt) => (
                    <option key={mt.id} value={mt.id}>{maintenanceTypeOptionLabel(mt)}</option>
                ))}
                </select>
            </div> 
            <div className="mt-3">
                <label className="block text-md font-semibold text-gray-800">{t('Common.MandatoryAttachment')}</label>
                {selectedCard?.maintenance_type?.use_central_required_attachment ? (
                    <>
                        <p className="mt-1 text-sm text-gray-600">{t('Ui.R67.Pouziva_sa_centralne_zadana_povinna_priloha_z_typu_udrzby')}</p>
                        <Checkbox
                            inputId="required_attachment_update"
                            checked={true}
                            className="mr-2 mt-2"
                            disabled
                        />
                        <label htmlFor="required_attachment_update" className="font-normal text-md text-gray-800 ml-2">{t('Common.Yes')}</label>
                    </>
                ) : (
                    <div className="flex items-center mt-2">
                        <Checkbox
                            inputId="required_attachment_update"
                            checked={Number(formikUpdate.values.required_attachment) === 1}
                            onChange={(e) => formikUpdate.setFieldValue("required_attachment", e.checked ? 1 : 0)}
                            className="mr-2"
                        />
                        <label htmlFor="required_attachment_update" className="font-normal text-md text-gray-800">{t('Common.MandatoryAttachment')}</label>
                    </div>
                )}
            </div>
            <div className="mt-3">
                <label
                    htmlFor="machine_part"
                    className="block text-md font-semibold text-gray-800"
                >{t('Common.MachinePart')}</label>
                <input 
                    name='machine_part'
                    type="text" required
                    onChange={formikUpdate.handleChange} value={formikUpdate.values.machine_part}
                    className="block w-full  px-4 py-2 mt-2 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                />
                {formikUpdate.errors?.machine_part && (
                    <div className="mt-1 text-sm text-red-600">{formikUpdate.errors.machine_part}</div>
                )}
            </div>
            <div className="mt-3">
                <label className="block text-md font-semibold text-gray-800">{t('Common.ResponsiblePerson')}</label>
                {selectedCard?.maintenance_type?.use_central_responsible_person ? (
                    <>
                        <p className="mt-1 text-sm text-gray-600">{t('Ui.R67.Je_nastavena_centralna_zodpovedna_osoba')}</p>
                        <Dropdown
                            name="responsible_employee_id"
                            value={selectedCard?.maintenance_type?.responsible_employee_id ?? selectedCard?.maintenance_type?.responsible?.id ?? null}
                            options={(state.employees || []).map(emp => ({ label: emp.name, value: emp.id }))}
                            placeholder={t('Common.SelectEmployee')}
                            className="w-full mt-2"
                            panelClassName="max-w-full"
                            disabled
                        />
                    </>
                ) : (
                    <Dropdown
                        name="responsible_employee_id" value={formikUpdate.values.responsible_employee_id || null}
                        onChange={(e) => formikUpdate.setFieldValue("responsible_employee_id", e.value)}
                        options={(state.employees || []).map(emp => ({ label: emp.name, value: emp.id }))}
                        placeholder={t('Common.SelectEmployee')}
                        filter showClear
                        className="w-full mt-2" panelClassName="max-w-full"
                    />
                )}
            </div>
            {selectedMtUpdate?.use_unified_template && (
                <div className="mt-3">
                    <label className="block text-md font-semibold text-gray-800">Predloha</label>
                    {templatePreview?.url ? (
                        <div className="mt-2 flex flex-row p-2">
                            <div className="cursor-pointer" onClick={() => openFilePreview({ url: templatePreview.url, filename: templatePreview.filename })}>
                                <embed src={templatePreview.url + '#view=FitH&scrollbar=0'} width="100px" height="65px" title={templatePreview.filename} />
                            </div>
                            <div className="flex flex-col ml-4">
                                <a style={{ color: 'blue', cursor: 'pointer', marginBottom: '2px' }} onClick={() => openFilePreview({ url: templatePreview.url, filename: templatePreview.filename })}>
                                    {templatePreview.filename}
                                </a>
                                <span className="text-sm text-gray-600">{t('Ui.R67.jednotna_predloha_klik_pre_nahlad')}</span>
                            </div>
                        </div>
                    ) : (
                        <p className="mt-1 text-sm text-gray-600">{t('Ui.R67.Pouziva_sa_jednotna_predloha_z_typu_udrzby')}</p>
                    )}
                </div>
            )}
            <MaintenanceTypeDetails mt={selectedMtUpdate} />
            <button type="submit" className='flex justify-center items-center mt-4 w-full h-10 font-medium mb-2 px-2 py-1 tracking-wide text-white transition-colors duration-200 transform bg-zinc-900 rounded-md hover:bg-zinc-700 focus:outline-none focus:bg-zinc-600 disabled:opacity-25'>{t('Common.Save')}</button>         
        </form>
    </Dialog>
        <Dialog header={t('MaintenanceTemplateRecordAdd.Text.Pridat_do_sablony')} visible={showTemplateRecordAdd} style={{ width: '40vw' }} onHide={() => setShowTemplateRecordAdd(false)}>
        <form onSubmit={formik.handleSubmit}>
            <div>
                <label
                    htmlFor="machineType"
                    className="block text-md font-semibold text-gray-800"
                >{t('MachineList.Column.MachineTypeName')}</label>
                <select 
                name='machineType'
                onChange={(e) => filterMaintenanceTypes(e.target.value)} value={machineType}
                className="bg-white block w-full  px-4 py-2 mt-2 text-grey-700 border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40">
                <option value="">{t('ServiceTaskDetail.SelectDepartment')}</option>
                {state.machine_types?.map((machine_type) => (
                    <option key={machine_type.id} value={machine_type.id}>{machine_type.name}</option>
                    ))}
                </select>
            </div>
            <div className='mt-3'>
                <label
                    htmlFor="maintenance_type_id"

                    className="block text-md font-semibold text-gray-800"
                >{t('Common.TaskName')}</label>
                <select required
                name='maintenance_type_id'
                onChange={(e) => {
                    const newId = e.target.value;
                    formik.setFieldValue("maintenance_type_id", newId);
                    const mt = getSelectedMaintenanceType(newId,null,maintenanceTypes,allMaintenanceTypes);
                    const respId = getResponsibleEmployeeIdFromMaintenanceType(mt);
                    formik.setFieldValue("responsible_employee_id", respId || "");
                }}
                value={formik.values.maintenance_type_id}
                className='block w-full px-4 py-2 mt-2 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40'>
                <option value="">{t('Ui.R67.Vyber_ukon')}</option>
                {maintenanceTypes.map((mt) => (
                    <option key={mt.id} value={mt.id}>{maintenanceTypeOptionLabel(mt)}</option>
                ))}
                </select>
            </div>
            <div className="mt-3">
                <label className="block text-md font-semibold text-gray-800">{t('Common.MandatoryAttachment')}</label>
                {(function() {
                    const mtAddReq = getSelectedMaintenanceType(formik.values.maintenance_type_id, null, maintenanceTypes, allMaintenanceTypes);
                    if (mtAddReq?.use_central_required_attachment) {
                        return (
                            <>
                                <p className="mt-1 text-sm text-gray-600">{t('Ui.R67.Pouziva_sa_centralne_zadana_povinna_priloha_z_typu_udrzby')}</p>
                                <Checkbox inputId="required_attachment_add" checked={true} className="mr-2 mt-2" disabled />
                                <label htmlFor="required_attachment_add" className="font-normal text-md text-gray-800 ml-2">{t('Common.Yes')}</label>
                            </>
                        );
                    }
                    return (
                        <div className="flex items-center mt-2">
                            <Checkbox
                                inputId="required_attachment_add"
                                checked={Number(formik.values.required_attachment) === 1}
                                onChange={(e) => formik.setFieldValue("required_attachment", e.checked ? 1 : 0)}
                                className="mr-2"
                            />
                            <label htmlFor="required_attachment_add" className="font-normal text-md text-gray-800">{t('Common.MandatoryAttachment')}</label>
                        </div>
                    );
                })()}
            </div>
            <div className="mt-3">
                <label
                    htmlFor="machine_part"
                    className="block text-md font-semibold text-gray-800"
                >{t('Common.MachinePart')}</label>
                <input 
                    name='machine_part'
                    type="text" required
                    onChange={formik.handleChange} value={formik.values.machine_part}
                    className="block w-full  px-4 py-2 mt-2 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                />
                {formik.errors?.machine_part && (
                    <div className="mt-1 text-sm text-red-600">{formik.errors.machine_part}</div>
                )}
            </div>
            <div className="mt-3">
                <label className="block text-md font-semibold text-gray-800">{t('Common.ResponsiblePerson')}</label>
                {(function() {
                    const mtAdd = getSelectedMaintenanceType(formik.values.maintenance_type_id, null, maintenanceTypes, allMaintenanceTypes);
                    if (mtAdd?.use_central_responsible_person) {
                        const centralRespId = mtAdd?.responsible_employee_id ?? mtAdd?.responsible?.id ?? null;
                        return (
                            <>
                                <p className="mt-1 text-sm text-gray-600">{t('Ui.R67.Je_nastavena_centralna_zodpovedna_osoba')}</p>
                                <Dropdown
                                    name="responsible_employee_id"
                                    value={centralRespId}
                                    options={(state.employees || []).map(emp => ({ label: emp.name, value: emp.id }))}
                                    placeholder={t('Common.SelectEmployee')}
                                    className="w-full mt-2"
                                    panelClassName="max-w-full"
                                    disabled
                                />
                            </>
                        );
                    }
                    return (
                        <Dropdown
                            name="responsible_employee_id" value={formik.values.responsible_employee_id || null}
                            onChange={(e) => formik.setFieldValue("responsible_employee_id", e.value)}
                            options={(state.employees || []).map(emp => ({ label: emp.name, value: emp.id }))}
                            placeholder={t('Common.SelectEmployee')}
                            filter showClear
                            className="w-full mt-2" panelClassName="max-w-full"
                        />
                    );
                })()}
            </div>
            <MaintenanceTypeDetails mt={selectedMtAdd} />
            <button type="submit" className='flex justify-center items-center mt-4 w-full h-10 font-medium mb-2 px-2 py-1 tracking-wide text-white transition-colors duration-200 transform bg-zinc-900 rounded-md hover:bg-zinc-700 focus:outline-none focus:bg-zinc-600 disabled:opacity-25'>{t('Common.Save')}</button>         
        </form>
        </Dialog>
        <Dialog header={t('Common.SetMask')} visible={maskVisible} style={{ width: '70vw' }} onHide={() => setMaskVisible(false)}>
          <div className="flex flex-col">
            <p className="text-sm text-gray-600 mb-2">{MASK_DIALOG_HELP_TEXT}</p>
            <div className="flex flex-wrap gap-2 mb-2">
              <button type="button" onClick={maskSelectAll} className="px-3 py-1.5 text-sm bg-zinc-200 text-zinc-900 rounded-md hover:bg-zinc-300">{t('Common.SelectAll')}</button>
              <button type="button" onClick={maskDeselectAll} className="px-3 py-1.5 text-sm bg-zinc-200 text-zinc-900 rounded-md hover:bg-zinc-300">{t('Common.DeselectAllExceptId')}</button>
            </div>
            {columns.map((item) => {
              if (!item?.field || MAINTENANCE_MASK_DIALOG_SKIP_FIELDS.has(item.field)) return null;
              const field = String(item.field);
              const inputId = `${MASK_PAGE}-${field}`;
              return (
                <div key={field} className="flex flex-row border border-black rounded-md p-2 m-1">
                  <MaintenanceMaskDialogCheckbox field={field} inputId={inputId} title={item.title} columnsVisible={columnsVisible} onChange={setMask} />
                  <label htmlFor={inputId} className={`ml-2 ${field === 'id' ? '' : 'cursor-pointer'}`}>{item.title}</label>
                </div>
              );
            })}
            <button type="button" onClick={saveUserMaskColumns} className="m-1 px-4 py-2 bg-zinc-900 text-white rounded-md hover:bg-zinc-700">{t('Common.SaveUpper')}</button>
          </div>
        </Dialog>
</div>
            </> 
            );
} 
